import { useEffect, useRef } from 'react';

/**
 * Automatically resizes a textarea based on content up to a maximum number of lines.
 * Returns a ref to attach to the textarea element for automatic height adjustment.
 */

// NOTE
// In the future this whole feature could be replaced using CSS field-sizing
// https://developer.mozilla.org/en-US/docs/Web/CSS/Reference/Properties/field-sizing
// Right now (march-2026) it's not supported by Firefox

interface UseAutoResizeTextareaOptions {
  value: string;
  maxLines?: number;
  lineHeight?: number;
}

export const useAutoResizeTextarea = ({
  value,
  maxLines = 4,
  lineHeight = 24,
}: UseAutoResizeTextareaOptions) => {
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    textarea.scrollTop = 0;
    textarea.style.height = 'auto';
    textarea.style.overflowY = 'hidden';

    const scrollHeight = textarea.scrollHeight;
    const maxHeight = lineHeight * maxLines;

    if (scrollHeight <= maxHeight) {
      textarea.style.height = `${scrollHeight}px`;
      textarea.style.overflowY = 'hidden';
    } else {
      textarea.style.height = `${maxHeight}px`;
      textarea.style.overflowY = 'auto';
    }
  }, [value, maxLines, lineHeight]);

  return textareaRef;
};
